<template>
  <div class="page faq">
    <b-container class="faq__container">
      <section class="section faq__header">
        <p class="faq__eyebrow">Domande frequenti</p>
        <h1 class="faq__title">Trova subito la risposta che cerchi</h1>
        <p class="faq__intro">
          L'87% dei clienti risolve dubbi su prenotazioni, check-in e modifiche leggendo le FAQ che seguono.<br />
          Dai un'occhiata alle categorie principali: se la risposta che cerchi non è tra queste, saremo felici di aiutarti direttamente!
        </p>

        <div class="faq__search">
          <vb-icon class="faq__search-icon" name="lens" :size="16" :color="sass('gray-600')" />
          <vb-input
            v-model="search"
            :vb_options="{
              type: 'search',
              placeholder: 'Cerca una domanda, ad esempio “prenotare volo”...',
              debounce: 200,
            }"
          />
        </div>

        <div v-if="named_categories.length" class="faq__anchors">
          <button
            v-for="category in named_categories"
            :key="category.catcode"
            type="button"
            class="faq__anchor"
            @click="scroll_to(category.catcode)"
          >
            {{ category.label }}
          </button>
        </div>
      </section>

      <loader v-if="loading" />

      <template v-else>
        <section
          v-for="category in visible_categories"
          :id="anchor_id(category.catcode)"
          :key="category.catcode"
          class="section faq__category"
        >
          <h2 v-if="category.label" class="faq__category-title">{{ category.label }}</h2>

          <vb-accordion :items="category.items" :accordion_id="`faq_${category.catcode}`">
            <template #answer="{ item }">
              <div class="faq__answer" v-html="item.answer"></div>
            </template>
          </vb-accordion>
        </section>

        <p v-if="search && !visible_categories.length" class="section text-center">
          Nessuna domanda corrisponde alla tua ricerca.
        </p>
      </template>

      <section class="section faq__contact">
        <b-row class="justify-content-center">
          <b-col md="8" lg="4">
            <feature-card
              class="shadow"
              icon="email"
              icon_background_color="#e9ecef"
              radius
              :title_size="18"
              title="Non hai trovato risposta?"
              text="Il nostro team prenderà in carico la tua richiesta"
              centered
            >
              <template #cta>
                <vb-button
                  :vb_options="{
                    variant: 'info',
                    size: 'lg',
                    text: 'Contatta l\'assistenza',
                    classes: 'semibold',
                    to: { name: 'contatti' },
                  }"
                />
              </template>
            </feature-card>
          </b-col>
        </b-row>
      </section>
    </b-container>
  </div>
</template>

<script>
import { service_handler } from '@/services'
import VbAccordion from '@library/blocks/accordion.vue'
import FeatureCard from '@components/blocks/feature-card.vue'

const normalize = (text) => (text || '')
  .replace(/<[^>]*>/g, ' ')
  .normalize('NFD')
  .replace(/[̀-ͯ]/g, '')
  .toLowerCase()

export default {
  name: 'faq',
  metaInfo: {
    title: 'Domande frequenti',
  },
  components: {
    'vb-accordion': VbAccordion,
    'feature-card': FeatureCard,
  },
  data() {
    return {
      loading: true,
      search: '',
      faqs: [],
    }
  },
  computed: {
    categories() {
      let counter = 0
      return this.faqs.map((faq) => {
        const label = faq.category
          ? `${String(++counter).padStart(2, '0')}. ${faq.category}`
          : ''
        return {
          catcode: faq.catcode,
          label,
          items: (faq.items || []).map((item) => ({
            question: item.title,
            answer: item.content,
          })),
        }
      })
    },
    named_categories() {
      return this.visible_categories.filter((category) => category.label)
    },
    visible_categories() {
      const query = normalize(this.search).trim()
      if (!query) return this.categories

      return this.categories
        .map((category) => ({
          ...category,
          items: category.items.filter((item) =>
            normalize(`${item.question} ${item.answer}`).includes(query)),
        }))
        .filter((category) => category.items.length)
    },
  },
  mounted() {
    this.get_faqs()
  },
  methods: {
    anchor_id(catcode) {
      return `faq-${catcode}`
    },
    scroll_to(catcode) {
      const section = document.getElementById(this.anchor_id(catcode))
      if (section) section.scrollIntoView({ behavior: 'smooth', block: 'start' })
    },
    get_faqs() {
      this.loading = true
      service_handler('getFaqs', {})
        .then(
          (success) => {
            this.log('[faq.vue] - success')

            this.faqs = success.data.faqs || []
            this.loading = false
          },
          (error) => {
            this.log('[faq.vue] - error')
            this.log(error.response)

            this.loading = false
          }
        )
    },
  },
}
</script>

<style lang="scss" scoped>
.faq {

  &__container {
    @extend %support-page-container;
  }

  &__header {
    margin-bottom: 0;
  }

  &__header + &__category {
    margin-top: $grid-gutter-width;
  }

  &__eyebrow {
    @extend %support-page-header__eyebrow;
  }

  &__title {
    @extend %support-page-header__title;
  }

  &__intro {
    margin-bottom: $grid-gutter-20;
    font-size: $font-size-base;
    line-height: 1.25;
    color: $gray-700;
  }

  &__search {
    position: relative;
    max-width: 850px;
    margin-bottom: $grid-gutter-20;

    ::v-deep .vb-input {
      height: 52px;
      padding-left: $grid-gutter-width * 1.25;
      font-size: $font-size-base;
      color: $gray-900;
      background-color: $white;
      border: 1px solid rgba($primary, .3);
      border-radius: $input-border-radius;

      &::placeholder {
        font-size: $font-size-base;
        color: $gray-600;
      }

      &:focus {
        border-color: $primary;
        box-shadow: none !important;
      }
    }
  }

  &__search-icon {
    position: absolute;
    top: 50%;
    left: $grid-gutter-width / 2;
    z-index: 1;
    transform: translateY(-50%);
  }

  &__anchors {
    display: flex;
    flex-wrap: wrap;
    gap: $grid-gutter-20 / 2;
    padding-bottom: $grid-gutter-width;
    border-bottom: 1px solid $gray-300;
  }

  &__anchor {
    height: 42px;
    padding: 0 $grid-gutter-width;
    font-size: $font-size-base;
    color: $gray-900;
    background-color: $white;
    border: 1px solid rgba($primary, .3);
    border-radius: $input-border-radius;
    transition: border-color .2s ease;

    &:hover,
    &:focus {
      border-color: $primary;
      outline: 0;
    }
  }

  &__category-title {
    margin-bottom: $grid-gutter-width / 2;
    font-size: $font-size-xl;
    font-weight: $vb-font-weight-semibold;
    color: $primary;
  }

  &__answer {
    white-space: pre-line;
  }

  &__contact {
    padding-top: $grid-gutter-width;
    border-top: 1px solid $gray-300;
  }
}
</style>

<style lang="scss">
.site-main:has(.faq__container) ~ .site-prefooter {
  display: none;
}
</style>
